import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, AlertCircle, Sun, Sunset, Moon, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import Loading from "../components/Loading";
import PrimaryButton, { GhostButton } from "../components/PrimaryButton";
import { validarTokenRemarcacao, confirmarRemarcacao } from "../lib/api";
import {
  groupSlotsByDay,
  periodOf,
  formatTime,
  formatDateTime,
} from "../lib/format";
import { setBooking } from "../lib/bookingState";

const PERIOD_META = {
  Manha: { label: "Manha", Icon: Sun },
  Tarde: { label: "Tarde", Icon: Sunset },
  Noite: { label: "Noite", Icon: Moon },
};

export default function Remarcar() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await validarTokenRemarcacao(token);
        if (!active) return;
        setState({
          loading: false,
          agendamento: data.agendamento_atual,
          groups: groupSlotsByDay(data.horarios || []),
        });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const reason = ["token_expirado", "token_invalido"].includes(detail) ? detail : "geral";
        navigate(`/erro?reason=${reason}`, { replace: true });
      }
    })();
    return () => {
      active = false;
    };
  }, [token, navigate]);

  async function handleConfirm() {
    if (!selected) return;
    setSubmitting(true);
    try {
      const r = await confirmarRemarcacao(token, selected);
      setBooking({ confirmacao: r.agendamento, data_hora: selected });
      toast.success("Remarcação concluída!");
      navigate("/sucesso?type=remarcacao", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "token_expirado") return navigate("/erro?reason=token_expirado", { replace: true });
      if (detail === "token_invalido") return navigate("/erro?reason=token_invalido", { replace: true });
      if (detail === "token_ja_utilizado") return navigate("/erro?reason=token_ja_utilizado", { replace: true });
      toast.error("Não foi possível concluir a remarcação. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  }

  if (state.loading) {
    return (
      <Layout>
        <Loading label="Validando seu link de remarcação..." />
      </Layout>
    );
  }

  const { agendamento, groups } = state;

  return (
    <Layout>
      <div className="fade-up">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EAF5F6] text-[#3D8E92] text-xs font-semibold">
          <RefreshCw size={13} /> Remarcação de consulta
        </div>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-bold text-slate-900 mt-3" style={{ fontFamily: "Manrope" }}>
          Escolha um novo horário
        </h1>
      </div>

      <section className="mt-5 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500">
          Consulta atual
        </div>
        <div className="mt-2 flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#E6EDF5] text-[#00357C] flex items-center justify-center shrink-0">
            <CalendarClock size={20} />
          </div>
          <div className="min-w-0">
            {agendamento?.paciente_nome && (
              <div className="font-semibold text-slate-900">{agendamento.paciente_nome}</div>
            )}
            {agendamento?.profissional_nome && (
              <div className="text-sm text-slate-700 mt-0.5">
                {agendamento.profissional_nome}
                {agendamento.especialidade_nome ? ` - ${agendamento.especialidade_nome}` : ""}
              </div>
            )}
            <div className="text-sm text-slate-500 mt-1">
              {formatDateTime(agendamento?.data_consulta) || "Data não disponível"}
            </div>
          </div>
        </div>
      </section>

      <h2 className="text-lg font-semibold text-slate-900 mt-7" style={{ fontFamily: "Manrope" }}>
        Horários disponíveis
      </h2>

      <div className="mt-4 pb-32">
        {groups.length === 0 && (
          <div className="bg-amber-50 text-amber-800 border border-amber-100 rounded-2xl px-4 py-4 text-sm flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5" />
            <div>Não há horários livres no momento. Entre em contato com a clínica.</div>
          </div>
        )}
        {groups.length > 0 && (
          <div className="space-y-5" data-testid="remarcar-horarios">
            {groups.map((g) => {
              const periods = { Manhã: [], Tarde: [], Noite: [] };
              for (const s of g.items) periods[periodOf(s.datetime)]?.push(s);
              return (
                <div key={g.day} className="bg-white border border-slate-200 rounded-2xl p-4">
                  <div className="font-semibold text-slate-900 capitalize" style={{ fontFamily: "Manrope" }}>
                    {g.label}
                  </div>
                  <div className="mt-3 space-y-3">
                    {Object.entries(periods).map(([key, list]) => {
                      if (!list.length) return null;
                      const Icon = PERIOD_META[key].Icon;
                      return (
                        <div key={key}>
                          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">
                            <Icon size={13} /> {PERIOD_META[key].label}
                          </div>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {list.map((s) => {
                              const isSel = selected === s.datetime;
                              return (
                                <button
                                  key={s.datetime}
                                  data-testid={`remarcar-slot-${s.datetime}`}
                                  onClick={() => setSelected(s.datetime)}
                                  className={[
                                    "h-12 rounded-xl text-sm font-medium border transition-all",
                                    isSel
                                      ? "bg-[#00357C] text-white border-[#00357C] shadow-sm"
                                      : "bg-white text-slate-700 border-slate-200 hover:border-[#00357C]/40 hover:text-[#00357C]",
                                  ].join(" ")}
                                >
                                  {formatTime(s.datetime)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur p-4">
        <div className="max-w-2xl mx-auto space-y-2">
          <PrimaryButton
            onClick={handleConfirm}
            disabled={!selected || submitting}
            data-testid="remarcar-confirmar"
          >
            {submitting ? "Confirmando..." : selected ? "Confirmar remarcação" : "Selecione um novo horário"}
          </PrimaryButton>
        </div>
      </div>
    </Layout>
  );
}
